// src/components/OrderDetails/OrderDetsilsScreen.js
import { RefreshCw, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import { getCurrentShiftForSite, getDishById, getDishesBySite, getTablesBySite } from '../PlaceOrder/placeOrderApi';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card, PrimaryButton, SearchBar } from '../ui';
import {
  deleteQueuedOrderItem,
  endOrder,
  getAllOrdersByShift,
  getOpenAndCloseOrders,
  getOrderItems,
} from './orderDetailsApi';

const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

const panelStyle = {
  background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(170deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.40) 97.94%)',
  boxShadow: '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  backdropFilter: 'blur(30px)',
};

// Mock table orders – replace with API when available
// const MOCK_TABLES = [
//   { id: '1', tableNumber: 1, time: '14:32', price: 128.50, isLive: true },
//   { id: '2', tableNumber: 2, time: '14:15', price: 45.00, isLive: true },
//   { id: '3', tableNumber: 3, time: '13:58', price: 92.00, isLive: true },
//   { id: '4', tableNumber: 4, time: '12:45', price: 67.25, isLive: false },
//   { id: '5', tableNumber: 5, time: '12:20', price: 156.00, isLive: false },
//   { id: '6', tableNumber: 6, time: '11:55', price: 38.50, isLive: false },
// ];

// Mock order items per table (tableId -> list of { id, dish, note, status })
// const MOCK_ORDER_ITEMS_BY_TABLE = {
//   '1': [
//     { id: 'o1-1', dish: 'Shrimp Basil Salad', note: 'No onions', status: 'Preparing' },
//     { id: 'o1-2', dish: 'Chicken Burger', note: '', status: 'Served' },
//     { id: 'o1-3', dish: 'Iced Coffee', note: 'Less ice', status: 'Pending' },
//   ],
//   '2': [
//     { id: 'o2-1', dish: 'Onion Rings', note: 'Extra sauce', status: 'Served' },
//     { id: 'o2-2', dish: 'Lemonade', note: '', status: 'Preparing' },
//   ],
//   '3': [
//     { id: 'o3-1', dish: 'Margherita Pizza', note: 'Well done', status: 'Pending' },
//   ],
//   '4': [
//     { id: 'o4-1', dish: 'Caesar Salad', note: '', status: 'Served' },
//     { id: 'o4-2', dish: 'Tomato Soup', note: '', status: 'Served' },
//   ],
//   '5': [],
//   '6': [{ id: 'o6-1', dish: 'Vegetable Rice', note: 'No spice', status: 'Served' }],
// };

const TABS = [
  { key: 'live', label: 'Live' },
  { key: 'ended', label: 'Ended' },
];

const OrderDetsilsScreen = () => {
  // Log screen view
  useScreenLogging('OrderDetsilsScreen', {
    component: 'OrderDetsilsScreen',
    screenType: 'order-details',
  });

  // Site and shift state
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState(null);
  const [ssId, setSsId] = useState(null);
  const [isLoadingShift, setIsLoadingShift] = useState(false);
  const [shiftError, setShiftError] = useState(null);

  // Orders and items state
  const [activeTab, setActiveTab] = useState('live');
  const [tableSearch, setTableSearch] = useState('');
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [orderItemsByTable, setOrderItemsByTable] = useState({});
  const [tables, setTables] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [deleteConfirmItemId, setDeleteConfirmItemId] = useState(null);
  const [isDeletingItem, setIsDeletingItem] = useState(false);
  const [isEndingOrder, setIsEndingOrder] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [refreshOrdersKey, setRefreshOrdersKey] = useState(0);

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch sites on mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError(null);
      try {
        const response = await getAllSites();
        const sitesList = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
          ? response
          : [];
        
        // Sort sites alphabetically by name
        const sortedSites = [...sitesList].sort((a, b) => {
          const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
          const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setSites(sortedSites);
        // Auto-select first site in alphabetical order as default
        if (sortedSites.length > 0 && !selectedSite) {
          setSelectedSite(sortedSites[0]);
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
        setSitesError(error.message || 'Failed to fetch sites');
      } finally {
        setIsSitesLoading(false);
      }
    };
    fetchSites();
  }, []);

  // Fetch current shift when site is selected
  useEffect(() => {
    const fetchShift = async () => {
      if (!selectedSite) {
        setSsId(null);
        setShiftError(null);
        return;
      }

      const siteId = selectedSite.site_id || selectedSite.id;
      if (!siteId) {
        console.error('Site ID not found');
        setSsId(null);
        setShiftError('Invalid site selected');
        return;
      }

      setIsLoadingShift(true);
      setShiftError(null);
      try {
        const shiftData = await getCurrentShiftForSite(siteId);
        
        // Note: 404 errors in console are expected when no shift exists
        // getCurrentShiftForSite returns null for 404s, which we handle gracefully below
        if (!shiftData) {
          setShiftError('No active shift found for this site today. Please ensure a shift is scheduled.');
          setSsId(null);
          return;
        }

        const shiftScheduleId = shiftData?.ss_id || shiftData?.shift_id || shiftData?.shift_schedule_id;
        if (shiftScheduleId) {
          setSsId(shiftScheduleId);
          setShiftError(null);
        } else {
          setShiftError('No active shift found for this site today. Please ensure a shift is scheduled.');
          setSsId(null);
        }
      } catch (error) {
        console.error('Error fetching shift:', error);
        setShiftError(error.message || 'Failed to fetch shift information. Please try again.');
        setSsId(null);
      } finally {
        setIsLoadingShift(false);
      }
    };

    fetchShift();
  }, [selectedSite]);

  // Fetch open and closed orders when shift is available
  useEffect(() => {
    const fetchOrders = async () => {
      if (!ssId) {
        setTables([]);
        setOrderItemsByTable({});
        return;
      }

      setIsLoadingOrders(true);
      setOrdersError(null);
      const siteIdForLog = selectedSite?.site_id || selectedSite?.id;
      // Backend expects ss_id with "ss_" prefix (same as place-order response)
      const ssIdForApi = ssId && !String(ssId).startsWith('ss_') ? `ss_${ssId}` : ssId;
      try {
        const ordersData = await getOpenAndCloseOrders(ssIdForApi);
        const raw = ordersData?.data ? ordersData.data : ordersData;

        // Diagnostic: log what the API returned so we can see why orders might be missing
        const openCount = (raw.open_orders || raw.open || raw.unfinished_orders || raw.unfinished || []).length;
        const closedCount = (raw.closed_orders || raw.closed || raw.finished_orders || raw.finished || []).length;
        const openIdsLen = Array.isArray(raw.open_order_ids) ? raw.open_order_ids.length : 0;
        const endedIdsLen = Array.isArray(raw.ended_order_ids) ? raw.ended_order_ids.length : 0;
        console.log('[Order Details] Fetched orders', {
          site_id: siteIdForLog,
          ss_id: ssIdForApi,
          response_keys: Object.keys(raw || {}),
          open_order_ids_count: openIdsLen,
          ended_order_ids_count: endedIdsLen,
          open_orders_count: openCount,
          closed_orders_count: closedCount,
        });
        
        // Log API event for fetching open and close orders
        loggingService.logInfo(
          'orders_open_close_fetch',
          `Fetched ${openCount} open and ${closedCount} closed orders for shift ${ssIdForApi}`,
          {
            endpoint: '/restaurant/order/open-and-close-orders',
            method: 'GET',
            siteId: siteIdForLog,
            shiftScheduleId: ssIdForApi,
            openOrdersCount: openCount,
            closedOrdersCount: closedCount,
            openOrderIdsCount: openIdsLen,
            endedOrderIdsCount: endedIdsLen,
          }
        ).catch(logError => {
          console.warn('[OrderDetsilsScreen] Failed to log event:', logError);
        });

        // Transform API response to table format
        // Backend can return: (1) full arrays open_orders/closed_orders, or (2) ID lists open_order_ids/ended_order_ids
        let openOrders = raw.open_orders || raw.open
          || raw.unfinished_orders || raw.unfinished || [];
        let closedOrders = raw.closed_orders || raw.closed
          || raw.finished_orders || raw.finished || [];

        // When API returns open_order_ids / ended_order_ids, fetch each order by ID
        const openIds = Array.isArray(raw.open_order_ids) ? raw.open_order_ids : [];
        const endedIds = Array.isArray(raw.ended_order_ids) ? raw.ended_order_ids : [];
        const idListToOrders = async (idList) => {
          const out = [];
          for (const id of (Array.isArray(idList) ? idList : []).slice(0, 50)) {
            const oid = typeof id === 'string' ? id : id?.order_id ?? id?.id;
            if (!oid) continue;
            try {
              const full = await getOrderItems(oid);
              const details = full.order_details || full;
              const itemsFromApi = full.order_items || full.items || [];
              const rawOrderData = details?.order_data;
              const orderData = Array.isArray(rawOrderData) && rawOrderData.length > 0 ? rawOrderData : itemsFromApi;
              out.push({
                order_id: full.order_id || oid,
                id: full.order_id || oid,
                order_details: details,
                order_items: itemsFromApi,
                order_data: orderData,
              });
              
              // Log API event for fetching order items (only log once per batch to avoid spam)
              if (idList.indexOf(oid) === 0) {
                loggingService.logInfo(
                  'order_items_fetch',
                  `Fetched order items for order ${oid}`,
                  {
                    endpoint: '/restaurant/order/items',
                    method: 'GET',
                    orderId: oid,
                    itemCount: itemsFromApi.length,
                  }
                ).catch(logError => {
                  console.warn('[OrderDetsilsScreen] Failed to log event:', logError);
                });
              }
            } catch {
              // skip failed single fetch
            }
          }
          return out;
        };
        if (openIds.length > 0 && openOrders.length === 0) {
          openOrders = await idListToOrders(openIds);
          console.log('[Order Details] Loaded', openOrders.length, 'open orders from open_order_ids');
        }
        if (endedIds.length > 0 && closedOrders.length === 0) {
          closedOrders = await idListToOrders(endedIds);
          console.log('[Order Details] Loaded', closedOrders.length, 'ended orders from ended_order_ids');
        }

        // If backend returns a single orders array, split by status
        if (openOrders.length === 0 && closedOrders.length === 0 && Array.isArray(raw.orders)) {
          const orders = raw.orders;
          openOrders = orders.filter((o) => {
            const status = (o.order_details || o).status ?? o.status;
            const s = String(status || '').toLowerCase();
            return s !== 'closed' && s !== 'ended' && s !== 'finished';
          });
          closedOrders = orders.filter((o) => {
            const status = (o.order_details || o).status ?? o.status;
            const s = String(status || '').toLowerCase();
            return s === 'closed' || s === 'ended' || s === 'finished';
          });
        }

        // Fallback: if still no open orders, try getall-orders-by-shift and fetch each order
        if (openOrders.length === 0 && activeTab === 'live') {
          console.log('[Order Details] No open orders; trying getall-orders-by-shift fallback, ss_id=', ssId);
          try {
            const orderIds = await getAllOrdersByShift(ssIdForApi);
            const idList = Array.isArray(orderIds) ? orderIds : [];
            console.log('[Order Details] getall-orders-by-shift returned', idList.length, 'order IDs');
            
            // Log API event for fetching all orders by shift
            loggingService.logInfo(
              'orders_by_shift_fetch',
              `Fetched ${idList.length} order IDs for shift ${ssIdForApi}`,
              {
                endpoint: '/restaurant/getall-orders-by-shift',
                method: 'GET',
                shiftScheduleId: ssIdForApi,
                orderCount: idList.length,
              }
            ).catch(logError => {
              console.warn('[OrderDetsilsScreen] Failed to log event:', logError);
            });
            
            const resolved = await idListToOrders(idList);
            if (resolved.length > 0) {
              openOrders = resolved;
              console.log('[Order Details] Fallback loaded', resolved.length, 'orders');
            }
          } catch (e) {
            console.warn('[Order Details] Fallback getall-orders-by-shift failed', e?.message || e);
          }
        }

        // Get orders for current tab
        const allOrders = activeTab === 'live' ? openOrders : closedOrders;
        console.log('[Order Details] Showing', allOrders.length, activeTab === 'live' ? 'live' : 'ended', 'orders');

        // Build table_id -> table_label map from GET /tables/tables so we show proper table names
        let tableIdToLabel = {};
        try {
          if (siteIdForLog && allOrders.some((o) => (o.order_details || o).table_id || o.table_id)) {
            const tablesList = await getTablesBySite(siteIdForLog);
            if (Array.isArray(tablesList)) {
              tableIdToLabel = tablesList.reduce((acc, t) => {
                if (t.table_id) acc[t.table_id] = t.table_label || (t.table_no != null ? `Table ${t.table_no}` : t.table_id);
                return acc;
              }, {});
            }
          }
        } catch (_) {
          // ignore tables fetch failure
        }

        // Build dish_id -> price and dish_id -> name maps from GET /restaurant/dish/getall-by-site.
        // Order items may use UUID or numeric/short id ("1", "9"); key by all possible identifiers.
        let dishIdToPrice = {};
        let dishIdToName = {};
        try {
          if (siteIdForLog) {
            const dishesList = await getDishesBySite(siteIdForLog);
            if (Array.isArray(dishesList)) {
              const priceVal = (d) => {
                const p = d.Price ?? d.price ?? d.dish_price ?? d.item_price ?? 0;
                return typeof p === 'number' ? p : parseFloat(p) || 0;
              };
              const nameVal = (d) =>
                d.dish_name ?? d.name ?? d.Name ?? d.item_name ?? d.dishName ?? 'Unnamed Dish';
              // 1) Map by dish's own ids (dish_id, id, item_id, dish_number, number, order_index, display_order)
              dishesList.forEach((d) => {
                const p = priceVal(d);
                const name = nameVal(d);
                const ids = [
                  d.dish_id, d.id, d.item_id,
                  d.dish_number, d.item_number, d.number, d.order_index, d.display_order,
                ].filter((x) => x != null && x !== '');
                ids.forEach((id) => {
                  const k = String(id);
                  dishIdToPrice[k] = p;
                  dishIdToName[k] = name;
                });
              });
              // 2) Fallback: when order uses "1","2" but API only has UUIDs, use 1-based position (only if key not set)
              dishesList.forEach((d, index) => {
                const p = priceVal(d);
                const name = nameVal(d);
                const k1 = String(index + 1);
                const k0 = String(index);
                if (dishIdToPrice[k1] === undefined) { dishIdToPrice[k1] = p; dishIdToName[k1] = name; }
                if (dishIdToPrice[k0] === undefined) { dishIdToPrice[k0] = p; dishIdToName[k0] = name; }
              });
            }
          }
        } catch (_) {
          // ignore dishes fetch failure
        }

        // 3) Fetch any missing dish_ids from order_data/order_items by ID so we get correct name & price (e.g. "1", "2")
        const allDishIds = new Set();
        allOrders.forEach((order) => {
          const od = order.order_details?.order_data ?? order?.order_data ?? [];
          const oi = order.order_items || order.order_data || [];
          [...od, ...oi].forEach((item) => {
            const id = item.dish_id ?? item.item_id ?? item.id;
            if (id != null && id !== '') allDishIds.add(String(id));
          });
        });
        for (const dishId of allDishIds) {
          if (dishIdToPrice[dishId] !== undefined) continue;
          try {
            const dish = await getDishById(dishId);
            const p = dish?.Price ?? dish?.price ?? dish?.dish_price ?? dish?.item_price ?? 0;
            const numP = typeof p === 'number' ? p : parseFloat(p) || 0;
            const name = dish?.dish_name ?? dish?.name ?? dish?.item_name ?? dish?.dishName ?? 'Unnamed Dish';
            dishIdToPrice[dishId] = numP;
            dishIdToName[dishId] = name;
          } catch (_) {}
        }

        // Debug: log price lookup data so you can check in console why total might be $0
        const dishCount = Object.keys(dishIdToPrice).length;
        const sampleDishIds = Object.keys(dishIdToPrice).slice(0, 5);
        const sampleDishPrices = sampleDishIds.map((id) => ({ id, price: dishIdToPrice[id] }));
        console.log('[Order Details] Price debug', {
          dishes_loaded: dishCount,
          sample_dish_ids_and_prices: sampleDishPrices,
          all_dish_ids: Object.keys(dishIdToPrice),
        });
        if (allOrders.length > 0) {
          const first = allOrders[0];
          const items = first.order_items || first.order_data || [];
          console.log('[Order Details] First order sample', {
            order_id: first.order_id || first.id,
            order_items_count: items.length,
            first_item_keys: items[0] ? Object.keys(items[0]) : [],
            first_item: items[0] || null,
            order_details_keys: first.order_details ? Object.keys(first.order_details) : [],
          });
        }

        const transformedTables = allOrders.map((order) => {
          // Handle different response structures
          const orderDetails = order.order_details || order;
          const eventTimestamp = orderDetails.event_timestamp || orderDetails.created_at || order.created_at || new Date().toISOString();
          const time = new Date(eventTimestamp).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          });

          // Total: prefer backend order-level total when present
          const orderData = orderDetails?.order_data ?? order?.order_data ?? [];
          const orderItems = (order.order_items && order.order_items.length > 0) ? order.order_items : orderData;
          const orderTotalRaw =
            orderDetails?.total ?? orderDetails?.order_total ?? orderDetails?.total_amount ?? orderDetails?.amount
            ?? orderDetails?.order_amount ?? orderDetails?.grand_total ?? orderDetails?.total_price
            ?? order?.total ?? order?.order_total ?? order?.grand_total ?? order?.total_price;
          let totalPrice =
            orderTotalRaw != null ? (typeof orderTotalRaw === 'number' ? orderTotalRaw : parseFloat(orderTotalRaw) || 0) : 0;

          // If no order-level total, use order_data (has dish_id + qty from place-order) with dish prices
          if ((!Number.isFinite(totalPrice) || totalPrice === 0) && Array.isArray(orderData) && orderData.length > 0 && Object.keys(dishIdToPrice).length > 0) {
            totalPrice = orderData.reduce((sum, line) => {
              const dishId = line.dish_id ?? line.item_id ?? line.id;
              const qty = line.qty ?? line.quantity ?? 1;
              const numQty = typeof qty === 'number' ? qty : parseInt(qty, 10) || 1;
              const unitPrice = dishId != null ? (dishIdToPrice[String(dishId)] ?? 0) : 0;
              return sum + (Number.isFinite(unitPrice) ? unitPrice * numQty : 0);
            }, 0);
          }
          // Else sum from order_items (item price or dish lookup; order_items may not have qty so default 1)
          if (!Number.isFinite(totalPrice) || totalPrice === 0) {
            totalPrice = orderItems.reduce((sum, item) => {
              const price = item.Price ?? item.price ?? item.unit_price ?? item.dish_price ?? item.item_price ?? item.price_per_unit ?? 0;
              const numPrice = typeof price === 'number' ? price : parseFloat(price);
              const qty = item.qty ?? item.quantity ?? 1;
              const numQty = typeof qty === 'number' ? qty : parseInt(qty, 10);
              return sum + (Number.isFinite(numPrice) ? numPrice * (Number.isFinite(numQty) ? numQty : 1) : 0);
            }, 0);
          }
          if (!Number.isFinite(totalPrice) || totalPrice === 0) {
            if (orderItems.length > 0 && Object.keys(dishIdToPrice).length > 0) {
              totalPrice = orderItems.reduce((sum, item) => {
                const dishId = item.dish_id ?? item.item_id ?? item.id;
                const qty = item.qty ?? item.quantity ?? 1;
                const numQty = typeof qty === 'number' ? qty : parseInt(qty, 10) || 1;
                const unitPrice = dishId != null ? (dishIdToPrice[String(dishId)] ?? 0) : 0;
                return sum + (Number.isFinite(unitPrice) ? unitPrice * numQty : 0);
              }, 0);
            }
          }

          // Table display: use table_no, or table_label from order, or lookup by table_id from tables API
          const tableNo = orderDetails.table_no ?? orderDetails.table_number ?? order.table_no;
          const tableId = orderDetails.table_id || order.table_id;
          const labelFromTables = tableId ? tableIdToLabel[tableId] : null;
          const tableLabel =
            orderDetails.table_label || labelFromTables || (tableNo != null ? `Table ${tableNo}` : (tableId ? tableId : '?'));

          return {
            id: order.order_id || order.id,
            orderId: order.order_id || order.id,
            tableNumber: tableNo != null ? tableNo : (labelFromTables || tableLabel),
            tableLabel: tableNo != null ? `Table ${tableNo}` : (labelFromTables ? labelFromTables : (tableId ? `Table ${tableId}` : `Order ${tableLabel}`)),
            time,
            price: totalPrice,
            isLive: activeTab === 'live',
            orderDetails,
          };
        });

        setTables(transformedTables);
        
        // Store order items by order ID for quick lookup
        // We'll fetch full details when an order is selected, but store basic info here
        const itemsMap = {};
        const getDishName = (item) => {
          const fromItem = item.dish_name ?? item.name ?? item.item_name ?? item.dishName;
          if (fromItem) return fromItem;
          const dishId = item.dish_id ?? item.item_id ?? item.id;
          return dishId != null ? (dishIdToName[String(dishId)] ?? 'Unnamed Dish') : 'Unnamed Dish';
        };
        allOrders.forEach((order) => {
          const orderId = order.order_id || order.id;
          const orderDetails = order.order_details || order;
          const orderData = orderDetails?.order_data ?? order?.order_data ?? [];
          const orderItems = (order.order_items && order.order_items.length > 0) ? order.order_items : orderData;
          
          // Merge order_items with order_data to preserve notes from order_data
          // Create a map of order_data items by dish_id for quick lookup
          const orderDataMap = new Map();
          orderData.forEach((dataItem) => {
            const dishId = dataItem.dish_id ?? dataItem.item_id ?? dataItem.id;
            if (dishId != null) {
              const key = String(dishId);
              if (!orderDataMap.has(key)) {
                orderDataMap.set(key, dataItem);
              }
            }
          });
          
          itemsMap[orderId] = orderItems.map((item) => {
            const dishId = item.dish_id ?? item.item_id ?? item.id;
            const dataItem = dishId != null ? orderDataMap.get(String(dishId)) : null;
            
            // Priority: Check cust_mods first (backend stores notes here), then notes, then other fields
            // Check all possible field names and locations
            // Note: cust_mods might be empty string '', so we need to check if it's truthy (non-empty)
            const note = (dataItem?.cust_mods && dataItem.cust_mods.trim()) ||
                        (dataItem?.notes && dataItem.notes.trim()) || 
                        (dataItem?.note && dataItem.note.trim()) ||
                        (item.cust_mods && item.cust_mods.trim()) ||
                        (item.notes && item.notes.trim()) || 
                        (item.note && item.note.trim()) ||
                        (item.customer_notes && item.customer_notes.trim()) || 
                        (item.special_instructions && item.special_instructions.trim()) || '';
            
            return {
              id: item.order_item_id || item.id,
              orderItemId: item.order_item_id || item.id,
              dish: getDishName(item),
              note,
              status: item.status || item.kitchen_stage || 'Pending',
              dishId,
            };
          });
        });
        setOrderItemsByTable(itemsMap);
      } catch (error) {
        console.error('Error fetching orders:', error);
        setOrdersError(error.message || 'Failed to fetch orders');
        setTables([]);
        setOrderItemsByTable({});
      } finally {
        setIsLoadingOrders(false);
      }
    };

    fetchOrders();
  }, [ssId, activeTab, refreshOrdersKey]);

  // Fetch full order details when a table/order is selected (and lookup dish names from dishes API if needed)
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!selectedTableId) return;

      try {
        const orderData = await getOrderItems(selectedTableId);
        const details = orderData.order_details || orderData;
        const rawOrderData = details?.order_data;
        const orderItems = (rawOrderData && rawOrderData.length > 0)
          ? rawOrderData
          : (orderData.order_items || orderData.items || []);
        
        // Log API event for fetching order items when table is selected
        loggingService.logInfo(
          'order_items_fetch',
          `Fetched order items for order ${selectedTableId}`,
          {
            endpoint: '/restaurant/order/items',
            method: 'GET',
            orderId: selectedTableId,
            itemCount: orderItems.length,
          }
        ).catch(logError => {
          console.warn('[OrderDetsilsScreen] Failed to log event:', logError);
        });

        let dishIdToName = {};
        const siteId = selectedSite?.site_id || selectedSite?.id;
        if (siteId && orderItems.some((i) => !(i.dish_name || i.name || i.item_name))) {
          try {
            const dishesList = await getDishesBySite(siteId);
            if (Array.isArray(dishesList)) {
              const nameVal = (d) => d.dish_name ?? d.name ?? d.Name ?? d.item_name ?? d.dishName ?? 'Unnamed Dish';
              dishesList.forEach((d) => {
                const name = nameVal(d);
                [d.dish_id, d.id, d.item_id, d.dish_number, d.item_number, d.number, d.order_index, d.display_order]
                  .filter((x) => x != null && x !== '').forEach((id) => { dishIdToName[String(id)] = name; });
              });
              dishesList.forEach((d, index) => {
                const name = nameVal(d);
                const k1 = String(index + 1); const k0 = String(index);
                if (dishIdToName[k1] === undefined) dishIdToName[k1] = name;
                if (dishIdToName[k0] === undefined) dishIdToName[k0] = name;
              });
            }
          } catch (_) {}
        }

        const getDishName = (item) => {
          const fromItem = item.dish_name ?? item.name ?? item.item_name ?? item.dishName;
          if (fromItem) return fromItem;
          const dishId = item.dish_id ?? item.item_id ?? item.id;
          return dishId != null ? (dishIdToName[String(dishId)] ?? 'Unnamed Dish') : 'Unnamed Dish';
        };

        // Merge order_items with order_data to preserve notes from order_data
        // Create a map of rawOrderData items by dish_id for quick lookup
        const orderDataMap = new Map();
        if (rawOrderData && Array.isArray(rawOrderData)) {
          rawOrderData.forEach((dataItem) => {
            const dishId = dataItem.dish_id ?? dataItem.item_id ?? dataItem.id;
            if (dishId != null) {
              const key = String(dishId);
              if (!orderDataMap.has(key)) {
                orderDataMap.set(key, dataItem);
              }
            }
          });
        }
        
        // Also check order_items from the API response
        const apiOrderItems = orderData.order_items || orderData.items || [];
        apiOrderItems.forEach((dataItem) => {
          const dishId = dataItem.dish_id ?? dataItem.item_id ?? dataItem.id;
          if (dishId != null) {
            const key = String(dishId);
            // Only add if not already in map (prioritize rawOrderData)
            if (!orderDataMap.has(key)) {
              orderDataMap.set(key, dataItem);
            }
          }
        });

        // Debug: Log order data structure to help diagnose note retrieval
        console.log('[Order Details] Fetching order details for', selectedTableId, {
          rawOrderData: rawOrderData,
          orderItems: orderItems,
          orderDataMap: Array.from(orderDataMap.entries()),
          apiOrderItems: apiOrderItems,
        });

        setOrderItemsByTable((prev) => ({
          ...prev,
          [selectedTableId]: orderItems.map((item) => {
            const dishId = item.dish_id ?? item.item_id ?? item.id;
            const dataItem = dishId != null ? orderDataMap.get(String(dishId)) : null;
            
            // Priority: Check cust_mods first (backend stores notes here), then notes, then other fields
            // Check all possible field names and locations
            // Note: cust_mods might be empty string '', so we need to check if it's truthy (non-empty)
            const note = (item.cust_mods && item.cust_mods.trim()) || 
                        (item.notes && item.notes.trim()) || 
                        (item.note && item.note.trim()) ||
                        (dataItem?.cust_mods && dataItem.cust_mods.trim()) ||
                        (dataItem?.notes && dataItem.notes.trim()) || 
                        (dataItem?.note && dataItem.note.trim()) ||
                        (item.customer_notes && item.customer_notes.trim()) || 
                        (item.special_instructions && item.special_instructions.trim()) || '';
            
            // Debug: Log note retrieval for each item (only if note exists to reduce console noise)
            if (note) {
              console.log('[Order Details] Found note for dish', dishId, ':', note, {
                itemCustMods: item.cust_mods,
                itemNotes: item.notes,
                itemNote: item.note,
                dataItemCustMods: dataItem?.cust_mods,
                dataItemNotes: dataItem?.notes,
                dataItemNote: dataItem?.note,
              });
            }
            
            return {
              id: item.order_item_id || item.id,
              orderItemId: item.order_item_id || item.id,
              dish: getDishName(item),
              note,
              status: item.status || item.kitchen_stage || 'Pending',
              dishId,
            };
          }),
        }));
      } catch (error) {
        console.error('Error fetching order details:', error);
      }
    };

    fetchOrderDetails();
  }, [selectedTableId, selectedSite]);

  const selectedTable = useMemo(
    () => (selectedTableId ? tables.find((t) => t.id === selectedTableId) : null),
    [selectedTableId, tables]
  );

  const orderItems = useMemo(
    () => (selectedTableId ? (orderItemsByTable[selectedTableId] || []) : []),
    [selectedTableId, orderItemsByTable]
  );

  const handleDeleteItem = useCallback(async (itemId) => {
    if (!selectedTableId || !itemId) return;

    setIsDeletingItem(true);
    try {
      // Find the order item ID from the item
      const orderItem = orderItems.find((i) => i.id === itemId);
      if (!orderItem || !orderItem.orderItemId) {
        throw new Error('Order item not found');
      }

      await deleteQueuedOrderItem({ order_id: selectedTableId, order_item_id: orderItem.orderItemId });

      // Log API event for deleting queued order item
      loggingService.logInfo(
        'order_item_delete',
        `Deleted queued order item ${orderItem.orderItemId} from order ${selectedTableId}`,
        {
          endpoint: '/restaurant/delete-order-item-if-status-queued',
          method: 'DELETE',
          orderId: selectedTableId,
          orderItemId: orderItem.orderItemId,
        }
      ).catch(logError => {
        console.warn('[OrderDetsilsScreen] Failed to log event:', logError);
      });

      const remainingItems = (orderItemsByTable[selectedTableId] || []).filter((i) => i.id !== itemId);
      const noItemsLeft = remainingItems.length === 0;

      // Remove item from local state
      setOrderItemsByTable((prev) => {
        const next = { ...prev, [selectedTableId]: remainingItems };
        if (noItemsLeft) {
          delete next[selectedTableId];
        }
        return next;
      });
      setDeleteConfirmItemId(null);

      // If no dishes left, remove the table/order from the list and clear selection
      if (noItemsLeft) {
        setTables((prev) => prev.filter((t) => t.id !== selectedTableId));
        setSelectedTableId(null);
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      alert(error.message || 'Failed to delete item. Please try again.');
    } finally {
      setIsDeletingItem(false);
    }
  }, [selectedTableId, orderItems, orderItemsByTable]);

  const handleEndOrder = useCallback(async () => {
    if (!selectedTableId) return;

    if (!confirm('Are you sure you want to end this order?')) {
      return;
    }

    setIsEndingOrder(true);
    try {
      await endOrder(selectedTableId);
      
      // Log API event for ending order
      loggingService.logInfo(
        'order_end',
        `Ended order ${selectedTableId}`,
        {
          endpoint: '/restaurant/order/end',
          method: 'POST',
          orderId: selectedTableId,
        }
      ).catch(logError => {
        console.warn('[OrderDetsilsScreen] Failed to log event:', logError);
      });
      
      // Refresh orders - the useEffect will automatically reload when activeTab changes
      // Switch to ended tab to show the ended order
      if (activeTab === 'live') {
        setActiveTab('ended');
      }
      
      // Clear selection
      setSelectedTableId(null);
      
      // Show success message
      alert('Order ended successfully');
    } catch (error) {
      console.error('Error ending order:', error);
      alert(error.message || 'Failed to end order. Please try again.');
    } finally {
      setIsEndingOrder(false);
    }
  }, [selectedTableId, activeTab]);

  const filteredTables = useMemo(() => {
    let list = tables.filter((t) =>
      activeTab === 'live' ? t.isLive : !t.isLive
    );
    if (tableSearch.trim()) {
      const term = tableSearch.trim().toLowerCase();
      list = list.filter((t) => {
        const label = (t.tableLabel || `Table ${t.tableNumber}`).toLowerCase();
        const num = String(t.tableNumber);
        return label.includes(term) || num.includes(term);
      });
    }
    return list;
  }, [activeTab, tableSearch, tables]);

  return (
    <div className="relative w-full h-full flex flex-col">
      <div
        className={combineClasses(
          'flex flex-1 w-full overflow-hidden gap-4',
          isMobileViewport ? 'flex-col overflow-y-auto' : 'flex-row'
        )}
      >
        {/* Left column: 400px desktop; full width mobile – tabs, search, table cards */}
        <Card
          variant="custom"
          className={combineClasses(
            'flex flex-col overflow-hidden flex-shrink-0',
            isMobileViewport ? 'w-full min-h-[280px]' : 'w-[400px]'
          )}
          style={panelStyle}
        >
          {/* Site selection */}
          <div className="p-4 border-b border-gray-100">
            <select
              value={selectedSite?.site_id || selectedSite?.id || ''}
              onChange={(e) => {
                const site = sites.find((s) => (s.site_id || s.id) === e.target.value);
                setSelectedSite(site || null);
                setSelectedTableId(null); // Clear selection when site changes
              }}
              className="w-full h-11 px-4 rounded-[10px] bg-white/80 border border-gray-200 text-gray-700 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400"
              disabled={isSitesLoading}
            >
              {/* <option value="">
                {isSitesLoading ? 'Loading sites...' : 'Select Site'}
              </option> */}
              {/* alphabetical order */}
              {Array.isArray(sites) && sites.map((site) => (
                <option key={site.site_id || site.id} value={site.site_id || site.id}>
                  {site.site_name || site.name || 'Unknown Site'}
                </option>
              ))}
            </select>
            {sitesError && (
              <div className="mt-2 p-2 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs">
                <p>{sitesError}</p>
              </div>
            )}
            {shiftError && (
              <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                <p>{shiftError}</p>
              </div>
            )}
            {isLoadingShift && (
              <div className="mt-2 p-2 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-xs">
                <p>Loading shift information...</p>
              </div>
            )}
          </div>

          {/* Tabs: Live | Ended */}
          <div className="flex border-b border-gray-100">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={combineClasses(
                  'flex-1 py-3 text-sm font-medium transition-colors',
                  typography.sh2,
                  activeTab === tab.key
                    ? 'bg-white/30 text-gray-900 border-b-2 border-blue-500'
                    : 'text-gray-600 hover:bg-white/10'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search and refresh */}
          <div className="p-4 border-b border-gray-100 flex gap-2 items-center">
            <SearchBar
              placeholder="Search table"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              iconType="search"
              className="flex-1 min-w-0 h-11 rounded-[10px] bg-white/80 border border-gray-200 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400"
              disabled={!ssId || isLoadingOrders}
            />
            <button
              type="button"
              onClick={() => setRefreshOrdersKey((k) => k + 1)}
              disabled={!ssId || isLoadingOrders}
              className="flex-shrink-0 p-2.5 rounded-[10px] bg-white/80 border border-gray-200 text-gray-700 hover:bg-white hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Refresh orders"
              aria-label="Refresh orders"
            >
              <RefreshCw className={combineClasses('w-5 h-5', isLoadingOrders && 'animate-spin')} />
            </button>
          </div>

          {/* Table cards list */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {!selectedSite && !isSitesLoading && (
              <p className={combineClasses('text-center text-gray-500 py-8', typography.p)}>
                Please select a site to view orders.
              </p>
            )}
            {selectedSite && !ssId && !isLoadingShift && (
              <p className={combineClasses('text-center text-gray-500 py-8', typography.p)}>
                No active shift found. Please ensure a shift is scheduled.
              </p>
            )}
            {selectedSite && ssId && isLoadingOrders && (
              <p className={combineClasses('text-center text-gray-500 py-8', typography.p)}>
                Loading orders...
              </p>
            )}
            {selectedSite && ssId && ordersError && (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                <p className="font-medium">Error loading orders</p>
                <p>{ordersError}</p>
              </div>
            )}
            {selectedSite && ssId && !isLoadingOrders && !ordersError && (
              <>
                {filteredTables.map((table) => (
                  <Card.Light
                    key={table.id}
                    onClick={() => setSelectedTableId(selectedTableId === table.id ? null : table.id)}
                    className={combineClasses(
                      'p-4 cursor-pointer border border-gray-200/80 hover:bg-white/90 transition-colors',
                      selectedTableId === table.id && 'ring-2 ring-blue-400 bg-white/90'
                    )}
                  >
                    {/* Row: Table (number) left, time right */}
                    <div className="flex items-center justify-between mb-2">
                      <span className={combineClasses('font-medium text-gray-900', typography.sh1)}>
                        {table.tableLabel || `Table ${table.tableNumber}`}
                      </span>
                      <span className={combineClasses('text-gray-600 text-sm', typography.sh3)}>
                        {table.time}
                      </span>
                    </div>
                    {/* Price below */}
                    <p className={combineClasses('text-red-600 font-semibold', typography.sh2)}>
                      ${table.price.toFixed(2)}
                    </p>
                  </Card.Light>
                ))}
                {filteredTables.length === 0 && (
                  <div className="text-center py-8 space-y-2">
                    <p className={combineClasses('text-gray-500', typography.p)}>
                      No {activeTab === 'live' ? 'live' : 'ended'} orders found.
                    </p>
                    <p className={combineClasses('text-gray-400 text-sm', typography.p)}>
                      {activeTab === 'live'
                        ? 'Select the same site where you placed the order, then use the refresh button.'
                        : ''}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>

        {/* Right column: flex desktop; full width below on mobile – table order summary */}
        <Card
          variant="custom"
          className={combineClasses(
            'flex flex-col overflow-hidden flex-shrink-0',
            isMobileViewport ? 'w-full flex-1 min-h-[320px]' : 'flex-1 min-w-0'
          )}
          style={panelStyle}
        >
          {selectedTableId ? (
            <>
              <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
                <h2 className={combineClasses('text-gray-800', typography.h3)}>
                  Order summary – {selectedTable?.tableLabel || `Table ${selectedTable?.tableNumber}`}
                </h2>
                {activeTab === 'live' && (
                  <PrimaryButton
                    type="button"
                    onClick={handleEndOrder}
                    disabled={isEndingOrder}
                    className="w-auto min-w-0 shrink-0"
                  >
                    {isEndingOrder ? 'Ending...' : 'End order'}
                  </PrimaryButton>
                )}
              </div>
              <div className="flex-1 overflow-auto p-4">
                {orderItems.length > 0 ? (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className={combineClasses('text-left py-3 px-2', typography.sh2)}>Dish</th>
                        <th className={combineClasses('text-left py-3 px-2', typography.sh2)}>Note</th>
                        <th className={combineClasses('text-left py-3 px-2', typography.sh2)}>Status</th>
                        {activeTab === 'live' && <th className="w-20 py-3 px-2" />}
                      </tr>
                    </thead>
                    <tbody>
                      {orderItems.map((item) => (
                        <tr key={item.id} className="border-b border-gray-100 hover:bg-white/30">
                          <td className={combineClasses('py-3 px-2 text-gray-900', typography.p)}>
                            {item.dish}
                          </td>
                          <td className={combineClasses('py-3 px-2 text-gray-600', typography.sh3)}>
                            {item.note || '–'}
                          </td>
                          <td className={combineClasses('py-3 px-2', typography.sh3)}>
                            <span
                              className={combineClasses(
                                'inline-block px-2 py-1 rounded text-sm',
                                (item.status === 'Served' || item.status === 'served') && 'bg-green-100 text-green-800',
                                (item.status === 'Preparing' || item.status === 'preparing' || item.status === 'in_progress') && 'bg-amber-100 text-amber-800',
                                (item.status === 'Pending' || item.status === 'pending' || item.status === 'queued') && 'bg-gray-100 text-gray-700',
                                (item.status === 'Ready' || item.status === 'ready') && 'bg-blue-100 text-blue-800'
                              )}
                            >
                              {item.status || 'Pending'}
                            </span>
                          </td>
                          {activeTab === 'live' && (
                            <td className="py-3 px-2">
                              <button
                                type="button"
                                onClick={() => setDeleteConfirmItemId(item.id)}
                                className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                                aria-label={`Delete ${item.dish}`}
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className={combineClasses('text-center text-gray-500 py-8', typography.p)}>
                    No items in this order.
                  </p>
                )}
                {orderItems.length > 0 && selectedTable?.price != null && Number.isFinite(selectedTable.price) && (
                  <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
                    <p className={combineClasses('text-gray-800 font-semibold', typography.sh2)}>
                      Total: ${Number(selectedTable.price).toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <h2 className={combineClasses('text-gray-600 mb-2', typography.h3)}>
                  Select a table
                </h2>
                <p className={combineClasses('text-gray-400', typography.p)}>
                  Choose a table from the list to view order details.
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Delete confirmation overlay */}
      {deleteConfirmItemId && (
        <div
          className="fixed inset-0 flex justify-center items-center z-[70] bg-black/30"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-title"
          onClick={() => setDeleteConfirmItemId(null)}
        >
          <div
            className="w-[400px] rounded-xl bg-white shadow-xl border border-gray-200 overflow-hidden p-6"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <h3 id="delete-confirm-title" className={combineClasses('text-gray-900 mb-2', typography.h3)}>
              Delete item?
            </h3>
            <p className={combineClasses('text-gray-600 mb-6', typography.p)}>
              This item will be removed from the order. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setDeleteConfirmItemId(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteItem(deleteConfirmItemId)}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetsilsScreen;
